import { useState } from 'react';
import { RefreshCw, Trash2, AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { cacheManager, formatBytes } from '@/utils/cacheManager';
import toast from 'react-hot-toast';

interface ClearCacheButtonProps {
  variant?: 'icon' | 'button' | 'full';
  className?: string;
}

export function ClearCacheButton({ variant = 'button', className = '' }: ClearCacheButtonProps) {
  const [showConfirm, setShowConfirm] = useState(false);
  const [cacheSize, setCacheSize] = useState<{ usage: number; quota: number } | null>(null);
  const [loading, setLoading] = useState(false);

  const handleCheckCache = async () => {
    const size = await cacheManager.getCacheSize();
    setCacheSize(size);
    setShowConfirm(true);
  };

  const handleClearCache = async () => {
    setLoading(true);
    const toastId = toast.loading('Clearing cache...');
    
    try {
      await cacheManager.clearAllCachesAndReload();
      toast.success('Cache cleared! Reloading...', { id: toastId });
    } catch (error) {
      console.error('Error clearing cache:', error);
      toast.error('Failed to clear cache. Reloading anyway...', { id: toastId });
      setTimeout(() => window.location.reload(), 1000);
    }
  };

  const handleSoftRefresh = () => {
    toast.loading('Refreshing...');
    cacheManager.softRefresh();
  };

  if (variant === 'icon') {
    return (
      <>
        <button
          onClick={handleCheckCache}
          className={`p-2 rounded-lg hover:bg-gray-100 transition-colors ${className}`}
          title="Clear cache and refresh"
        >
          <RefreshCw className="h-5 w-5 text-gray-600" />
        </button>
        <ConfirmDialog
          show={showConfirm}
          onClose={() => setShowConfirm(false)}
          onConfirm={handleClearCache}
          onSoftRefresh={handleSoftRefresh}
          cacheSize={cacheSize}
          loading={loading}
        />
      </>
    );
  }

  if (variant === 'button') {
    return (
      <>
        <button
          onClick={handleCheckCache}
          className={`flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors ${className}`}
        >
          <RefreshCw className="h-4 w-4" />
          <span className="text-sm font-medium">Clear Cache</span>
        </button>
        <ConfirmDialog
          show={showConfirm}
          onClose={() => setShowConfirm(false)}
          onConfirm={handleClearCache}
          onSoftRefresh={handleSoftRefresh}
          cacheSize={cacheSize}
          loading={loading}
        />
      </>
    );
  }

  // Full variant with info
  return (
    <>
      <div className={`bg-white rounded-xl p-4 border border-gray-200 ${className}`}>
        <div className="flex items-start gap-3">
          <div className="p-2 bg-blue-50 rounded-lg">
            <RefreshCw className="h-5 w-5 text-brand-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900 mb-1">Clear Cache</h3>
            <p className="text-sm text-gray-600 mb-3">
              If you're not seeing the latest updates, clear your browser cache to reload fresh content.
            </p>
            <div className="flex gap-2">
              <button
                onClick={handleSoftRefresh}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors"
              >
                Quick Refresh
              </button>
              <button
                onClick={handleCheckCache}
                className="px-4 py-2 bg-brand-500 hover:bg-brand-600 text-white rounded-lg text-sm font-medium transition-colors"
              >
                Clear All Cache
              </button>
            </div>
          </div>
        </div>
      </div>
      <ConfirmDialog
        show={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={handleClearCache}
        onSoftRefresh={handleSoftRefresh}
        cacheSize={cacheSize}
        loading={loading}
      />
    </>
  );
}

interface ConfirmDialogProps {
  show: boolean;
  onClose: () => void;
  onConfirm: () => void;
  onSoftRefresh: () => void;
  cacheSize: { usage: number; quota: number } | null;
  loading: boolean;
}

function ConfirmDialog({ show, onClose, onConfirm, onSoftRefresh, cacheSize, loading }: ConfirmDialogProps) {
  return (
    <AnimatePresence>
      {show && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/30 z-50 backdrop-blur-sm"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-white rounded-2xl shadow-2xl z-50 p-6"
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="p-2 bg-amber-50 rounded-lg">
                <AlertCircle className="h-6 w-6 text-amber-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-gray-900 mb-1">Clear Cache?</h3>
                <p className="text-sm text-gray-600">
                  This will clear all cached data and reload the page. You'll see the latest updates.
                </p>
              </div>
            </div>

            {cacheSize && (
              <div className="bg-gray-50 rounded-lg p-3 mb-4">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Cache Usage:</span>
                  <span className="font-medium text-gray-900">
                    {formatBytes(cacheSize.usage)} / {formatBytes(cacheSize.quota)}
                  </span>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <button
                onClick={onConfirm}
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 bg-brand-500 hover:bg-brand-600 disabled:bg-gray-300 text-white font-semibold py-3 rounded-xl transition-colors"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Clearing...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4" />
                    Clear All Cache
                  </>
                )}
              </button>
              
              <button
                onClick={onSoftRefresh}
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 text-gray-700 font-medium py-3 rounded-xl transition-colors"
              >
                <RefreshCw className="h-4 w-4" />
                Just Refresh
              </button>

              <button
                onClick={onClose}
                disabled={loading}
                className="w-full text-gray-600 hover:text-gray-800 font-medium py-2 transition-colors"
              >
                Cancel
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
